import React, { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import outlookIcon from '~/assets/public/outlook-icon.svg';
import { Button, DateTime, FormMessage, Icon } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import DisconnectCalendar from './DisconnectCalendar';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: ${weights.light};
  line-height: 1.2;
`;

const Description = styled.p`
  max-width: 41.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${colors.grey55};
`;

const Providers = styled.div`
  max-width: 35rem;
  margin-top: 2rem;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background-color: ${({ $isConnected }) => ($isConnected ? colors.primary5 : colors.white)};
  border: 1px solid ${({ $isConnected }) => ($isConnected ? colors.primary25 : colors.grey10)};
  border-radius: 0.3125rem;
`;

const Logo = styled.img`
  width: 2.25rem;
  height: 2.25rem;
`;

const Details = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: ${weights.bold};
  color: ${colors.black};

  svg {
    color: ${colors.primary};
  }
`;

const Subtext = styled.div`
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const ConnectButton = styled.a`
  flex-shrink: 0;
`;

const Notes = styled.ul`
  margin-top: 0.75rem;
  padding-left: 1.25rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
  list-style: disc;

  li:not(:last-child) {
    margin-bottom: 0.25rem;
  }
`;

const ReconnectNote = styled.p`
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

export default function Calendar() {
  useDocumentTitle('Calendar');

  const api = useApi();
  const { workspace } = useWorkspace();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const fetchIntegration = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage();
    try {
      const { data } = await api.www.workspaces(workspace.id).microsoftCalendar.getIntegration();
      if (isMounted.current) setIntegration(data ?? null);
    } catch ({ message }) {
      if (isMounted.current)
        setErrorMessage(message || 'Your connected calendar could not be loaded. Please try again.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [api, workspace, isMounted]);

  useEffect(() => {
    fetchIntegration();
  }, [fetchIntegration]);

  if (isLoading) return <PageLoader />;

  if (integration && !integration.isWorkspaceConnected) return <Redirect to={`/app/${workspace.key}/my-profile`} />;

  const canConnect = !!integration?.isWorkspaceConnected && !integration?.isConnected;

  return (
    <Wrapper>
      <div>
        <Title>Calendar</Title>
        <Description>
          Connect your calendar and your meetings show up when you track time. Pick one, and Ruddr fills in the hours,
          the notes and, when it can, the project.
        </Description>
      </div>

      {errorMessage && <FormMessage.Error spaceTop>{errorMessage}</FormMessage.Error>}

      {integration && (
        <Providers>
          {integration.isConnected ? (
            <>
              <Card $isConnected data-testid="connected-calendar">
                <Logo src={outlookIcon} alt="" />
                <Details>
                  <Name>
                    <Icon icon="check-circle" />
                    Outlook
                  </Name>
                  <Subtext>
                    {integration.email}
                    {integration.connectedAt && (
                      <>
                        {integration.email ? ', connected ' : 'Connected '}
                        <DateTime value={integration.connectedAt} />
                      </>
                    )}
                  </Subtext>
                </Details>
                {!integration.isImpersonating && (
                  <Button isAnchor onClick={() => setIsDisconnecting(true)}>
                    Disconnect
                  </Button>
                )}
              </Card>
              <Notes>
                <li>Events appear in the time entry drawer for the entry's date.</li>
                <li>Out-of-office and all-day events are offered as time off.</li>
                <li>Ruddr stores only the event id and date with each time entry.</li>
              </Notes>
            </>
          ) : (
            <>
              <Card>
                <Logo src={outlookIcon} alt="" />
                <Details>
                  <Name>Outlook</Name>
                  <Subtext>Reads your default Microsoft 365 calendar.</Subtext>
                </Details>
                {integration.tokenStatusId && !integration.isImpersonating && (
                  <Button isAnchor onClick={() => setIsDisconnecting(true)}>
                    Disconnect
                  </Button>
                )}
                {canConnect && integration.authorizeUri && (
                  <ConnectButton className="button" href={integration.authorizeUri} data-testid="calendar-connect">
                    Connect
                  </ConnectButton>
                )}
              </Card>
              {integration.needsReconnect && (
                <ReconnectNote data-testid="calendar-reconnect">
                  Microsoft no longer accepts Ruddr's access to {integration.email ?? 'your calendar'}. Connect it again
                  to see your events when you track time.
                </ReconnectNote>
              )}
            </>
          )}
        </Providers>
      )}

      {isDisconnecting && (
        <DisconnectCalendar
          email={integration?.email}
          onClose={() => setIsDisconnecting(false)}
          onDisconnect={() => fetchIntegration()}
        />
      )}
    </Wrapper>
  );
}
